import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseUtils';
import './CommunicationLog.css';

const CommunicationLog = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formValues, setFormValues] = useState({
    type: 'call',
    contact_id: '',
    contact_name: '',
    team_id: '',
    team_name: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    follow_up_date: '',
    follow_up_notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchCommunications();
  }, []);

  const fetchCommunications = async () => {
    setLoading(true);
    try {
      // Mock communication data for development
      const mockCommunications = [
        {
          id: '1',
          type: 'call',
          contact_id: '1',
          contact_name: 'Sarah Johnson',
          team_id: '1',
          team_name: 'Denver Vikings',
          date: '2023-05-20',
          notes: 'Discussed upcoming tournament in Denver. They are interested in having team photos and individual player portraits.',
          follow_up_date: '2023-06-15',
          follow_up_notes: 'Call back to confirm package details',
          created_by: 'Mark Wilson',
          created_at: '2023-05-20T14:30:00Z'
        },
        {
          id: '2',
          type: 'email',
          contact_id: '2',
          contact_name: 'Michael Rodriguez',
          team_id: '2',
          team_name: 'Colorado Rapids Youth',
          date: '2023-05-18',
          notes: 'Sent information about our packages for youth soccer programs. Included pricing details and sample galleries.',
          follow_up_date: '2023-06-17',
          follow_up_notes: 'Follow up if no response',
          created_by: 'Jennifer Lee',
          created_at: '2023-05-18T09:15:00Z'
        },
        {
          id: '3',
          type: 'meeting',
          contact_id: '5',
          contact_name: 'Amanda Chen',
          team_id: '3',
          team_name: 'Boulder Basketball Club',
          date: '2023-05-15',
          notes: 'Met in person to discuss coverage for their upcoming tournament. They need photographers for all 3 days, with quick turnaround for digital delivery.',
          follow_up_date: '2023-06-21',
          follow_up_notes: 'Send formal proposal',
          created_by: 'Mark Wilson',
          created_at: '2023-05-15T16:45:00Z'
        },
        {
          id: '4',
          type: 'text',
          contact_id: '4',
          contact_name: 'David Thompson',
          team_id: '4',
          team_name: 'Eagles High School Athletics',
          date: '2023-05-10',
          notes: 'Texted to confirm receipt of our proposal. He acknowledged and said they will review this week.',
          follow_up_date: '2023-06-18',
          follow_up_notes: 'Check on proposal status',
          created_by: 'Jennifer Lee',
          created_at: '2023-05-10T10:30:00Z'
        },
        {
          id: '5',
          type: 'other',
          contact_id: '3',
          contact_name: 'Jessica Williams',
          team_id: '',
          team_name: 'Colorado State Championship',
          date: '2023-05-05',
          notes: 'Initial contact at networking event. Expressed interest in having us as the official photographer for state championships.',
          follow_up_date: '2023-06-10',
          follow_up_notes: 'Call to set up formal meeting',
          created_by: 'Mark Wilson',
          created_at: '2023-05-05T18:20:00Z'
        }
      ];
      
      setCommunications(mockCommunications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching communications:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create new communication record
      const newCommunication = {
        id: crypto.randomUUID(),
        ...formValues,
        created_by: 'Current User', // In production, this would be the logged-in user
        created_at: new Date().toISOString()
      };
      
      setCommunications([newCommunication, ...communications]);
      
      // In production, you would insert into Supabase
      // const { error } = await supabase
      //   .from('communications')
      //   .insert(newCommunication);
      
      resetForm();
      alert('Communication logged successfully!');
    } catch (error) {
      console.error('Error saving communication:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      type: 'call',
      contact_id: '',
      contact_name: '',
      team_id: '',
      team_name: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      follow_up_date: '',
      follow_up_notes: ''
    });
    setShowForm(false);
  };

  const handleDeleteCommunication = async (communicationId) => {
    if (!confirm("Are you sure you want to delete this communication record?")) {
      return;
    }
    
    try {
      // Remove from UI for immediate feedback
      setCommunications(communications.filter(comm => comm.id !== communicationId));
      
      // In production, you would delete from Supabase
      alert("Communication record deleted successfully");
    } catch (error) {
      console.error("Error deleting communication:", error);
      alert(`Error: ${error.message}`);
      fetchCommunications(); // Refresh to restore data
    }
  };

  // Filter communications by type and search term
  const filteredCommunications = communications.filter(comm => {
    const matchesSearch = 
      (comm.contact_name && comm.contact_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (comm.team_name && comm.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (comm.notes && comm.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (comm.created_by && comm.created_by.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || comm.type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get icon for communication type
  const getTypeIcon = (type) => {
    const icons = {
      'call': 'fa-phone',
      'email': 'fa-envelope',
      'meeting': 'fa-handshake',
      'text': 'fa-comment-alt',
      'other': 'fa-comment-dots'
    };
    
    return icons[type] || 'fa-comment';
  };

  // Get label for communication type
  const getTypeLabel = (type) => {
    const labels = {
      'call': 'Phone Call',
      'email': 'Email',
      'meeting': 'Meeting',
      'text': 'Text Message',
      'other': 'Other'
    };
    
    return labels[type] || type;
  };

  return (
    <div className="communications-container">
      <div className="section-header">
        <h3>Communication Log</h3>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchCommunications}
            title="Refresh Communications"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button 
            className="add-button"
            onClick={() => setShowForm(true)}
          >
            <i className="fas fa-plus-circle"></i> Log Communication
          </button>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="comms-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search communications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="call">Calls</option>
            <option value="email">Emails</option>
            <option value="meeting">Meetings</option>
            <option value="text">Text Messages</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      
      {/* Communications List */}
      {loading && communications.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading communications...</p>
        </div>
      ) : filteredCommunications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-comments"></i>
          </div>
          <h3>No Communications Found</h3>
          <p>{searchTerm || filterType !== 'all' ? 'Try adjusting your search or filters' : 'Log your first communication to get started'}</p>
          {!searchTerm && filterType === 'all' && (
            <button 
              className="add-button"
              onClick={() => setShowForm(true)}
            >
              <i className="fas fa-plus-circle"></i> Log Communication
            </button>
          )}
        </div>
      ) : (
        <div className="communications-timeline">
          {filteredCommunications.map(comm => (
            <div className="timeline-item" key={comm.id}>
              <div className={`timeline-icon ${comm.type}`}>
                <i className={`fas ${getTypeIcon(comm.type)}`}></i>
              </div>
              
              <div className="timeline-content">
                <div className="timeline-header">
                  <div className="timestamp">
                    <i className="fas fa-calendar-alt"></i> {formatDate(comm.date)}
                  </div>
                  
                  <div className="comm-type">
                    {getTypeLabel(comm.type)}
                  </div>
                  
                  <div className="timeline-actions">
                    <button 
                      className="action-button delete"
                      onClick={() => handleDeleteCommunication(comm.id)}
                      title="Delete Record"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                
                <div className="contact-info">
                  {comm.contact_name && (
                    <div className="contact-name">
                      <i className="fas fa-user"></i> {comm.contact_name}
                    </div>
                  )}
                  
                  {comm.team_name && (
                    <div className="team-name">
                      <i className="fas fa-users"></i> {comm.team_name}
                    </div>
                  )}
                </div>
                
                <div className="timeline-notes">
                  <p>{comm.notes}</p>
                </div>
                
                {comm.follow_up_date && (
                  <div className="follow-up-info">
                    <div className="follow-up-header">
                      <i className="fas fa-bell"></i> Follow-up
                    </div>
                    <div className="follow-up-date">
                      <span className={new Date(comm.follow_up_date) < new Date() ? 'overdue' : ''}>
                        {formatDate(comm.follow_up_date)}
                      </span>
                    </div>
                    {comm.follow_up_notes && (
                      <div className="follow-up-notes">{comm.follow_up_notes}</div>
                    )}
                  </div>
                )}
                
                <div className="timeline-footer">
                  <div className="created-by">
                    <i className="fas fa-user-edit"></i> {comm.created_by}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Communication Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="comm-form-modal">
            <div className="modal-header">
              <h3>Log Communication</h3>
              <button 
                className="close-button"
                onClick={resetForm}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Communication Type</label>
                  <select
                    name="type"
                    value={formValues.type}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  >
                    <option value="call">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="text">Text Message</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formValues.date}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formValues.contact_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Who did you communicate with?"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Team/Organization</label>
                  <input
                    type="text"
                    name="team_name"
                    value={formValues.team_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Related team or organization"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formValues.notes}
                  onChange={handleInputChange}
                  rows="4"
                  required
                  disabled={loading}
                  placeholder="What was discussed? Any outcomes or action items?"
                ></textarea>
              </div>
              
              <div className="form-section">
                <h4>Follow-up</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Follow-up Date</label>
                    <input
                      type="date"
                      name="follow_up_date"
                      value={formValues.follow_up_date}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Follow-up Notes</label>
                  <textarea
                    name="follow_up_notes"
                    value={formValues.follow_up_notes}
                    onChange={handleInputChange}
                    rows="2"
                    disabled={loading}
                    placeholder="What needs to be done next?"
                  ></textarea>
                </div>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">
                      <span className="spinner-small"></span>
                      Saving...
                    </span>
                  ) : (
                    'Save Communication'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationLog; 